import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, ArrowUp, AlertTriangle, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const QUICK_TOPICS = [
  { label: "Organisation de l'USN France", msg: "Quelle est l'organisation de l'USN Section France ?" },
  { label: "Règlement intérieur", msg: "Quels sont les principaux points du règlement intérieur ?" },
  { label: "Vision du Chef de l'État", msg: "Quelle est la vision du Président Félix Tshisekedi ?" },
  { label: "Adhésion / Espace membre", msg: "Comment devenir membre de l'USN Section France ?" },
  { label: "Naviguer sur le site", msg: "Peux-tu m'expliquer comment naviguer sur cette plateforme ?" },
];

const AssistantWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => setShowPopup(true), 8000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (text) => {
    if (!text.trim() || loading) return;
    setShowPopup(false);
    const userMsg = { role: 'user', content: text };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/assistant/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, session_id: sessionId }),
      });
      const data = await res.json();
      if (data.session_id) setSessionId(data.session_id);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.response,
        needs_escalation: data.needs_escalation,
      }]);
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "Désolé, une erreur est survenue. Veuillez réessayer.",
        needs_escalation: true,
      }]);
    }
    setLoading(false);
  };

  const escalate = async () => {
    if (!sessionId) return;
    try {
      await fetch(`${API_URL}/api/assistant/escalate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, reason: "Question complexe remontée par le visiteur" }),
      });
      setMessages(prev => [...prev, {
        role: 'system',
        content: "Votre question a été transmise à l'administration. Vous recevrez une réponse prochainement.",
      }]);
    } catch {}
  };

  return (
    <>
      {/* Popup d'aide */}
      {showPopup && !open && (
        <div
          className="fixed bottom-24 right-4 sm:right-6 z-50 bg-white rounded-2xl shadow-2xl border border-gray-100 p-4 max-w-[280px] animate-in slide-in-from-bottom-4"
          data-testid="assistant-popup"
        >
          <button onClick={() => setShowPopup(false)} className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 text-sm">
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-[#005CB6] flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <p className="text-sm font-bold text-[#0D1F53]">Besoin d'aide ?</p>
          </div>
          <p className="text-xs text-gray-600 mb-3">Je peux répondre à vos questions sur l'USN Section France, le règlement, ou la navigation.</p>
          <button
            onClick={() => { setShowPopup(false); setOpen(true); }}
            className="w-full bg-[#005CB6] text-white text-xs font-semibold py-2 rounded-lg hover:bg-[#004a94] transition-colors"
          >
            Poser une question
          </button>
        </div>
      )}

      {/* Bouton flottant */}
      <button
        onClick={() => { setOpen(!open); setShowPopup(false); }}
        className={`fixed bottom-4 right-4 sm:right-6 z-50 w-14 h-14 rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 ${
          open ? 'bg-gray-800 hover:bg-gray-700 rotate-90' : 'bg-[#005CB6] hover:bg-[#004a94] hover:scale-110'
        }`}
        data-testid="assistant-toggle"
      >
        {open ? <X className="w-6 h-6 text-white" /> : <MessageCircle className="w-6 h-6 text-white" />}
        {!open && messages.length === 0 && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#FFDA00] rounded-full animate-pulse" />
        )}
      </button>

      {/* Chat Window */}
      {open && (
        <div
          className="fixed bottom-20 right-4 sm:right-6 z-50 w-[calc(100vw-2rem)] sm:w-[380px] h-[520px] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
          data-testid="assistant-chat"
        >
          {/* Header */}
          <div className="bg-[#0D1F53] px-4 py-3.5 flex items-center gap-3 flex-shrink-0">
            <div className="w-9 h-9 rounded-full bg-[#005CB6] flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-white font-bold text-sm">Assistant USN France</h3>
              <p className="text-white/60 text-[11px]">Posez vos questions</p>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
            {messages.length === 0 && (
              <div className="space-y-3">
                <p className="text-center text-sm text-gray-500 mb-4">Bonjour ! Comment puis-je vous aider ?</p>
                {QUICK_TOPICS.map((topic, i) => (
                  <button
                    key={i}
                    onClick={() => { setOpen(true); sendMessage(topic.msg); }}
                    className="w-full text-left bg-white border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm text-[#0D1F53] hover:border-[#005CB6] hover:bg-blue-50/50 transition-all duration-200"
                    data-testid={`quick-topic-${i}`}
                  >
                    {topic.label}
                  </button>
                ))}
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-[#005CB6] text-white rounded-br-md'
                    : msg.role === 'system'
                    ? 'bg-[#FFDA00]/20 text-[#0D1F53] border border-[#FFDA00]/40'
                    : 'bg-white text-gray-800 shadow-sm border border-gray-100 rounded-bl-md'
                }`}>
                  <p className="whitespace-pre-wrap">{msg.content}</p>
                  {msg.needs_escalation && msg.role === 'assistant' && (
                    <button
                      onClick={escalate}
                      className="mt-2 flex items-center gap-1.5 text-xs text-orange-600 hover:text-orange-800 font-semibold transition-colors"
                      data-testid="escalate-btn"
                    >
                      <AlertTriangle className="w-3.5 h-3.5" />
                      Transmettre à l'administration
                    </button>
                  )}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-white rounded-2xl px-4 py-3 shadow-sm border border-gray-100 flex items-center gap-1.5">
                  <span className="w-2 h-2 bg-[#005CB6] rounded-full animate-bounce" style={{animationDelay: '0ms'}} />
                  <span className="w-2 h-2 bg-[#005CB6] rounded-full animate-bounce" style={{animationDelay: '150ms'}} />
                  <span className="w-2 h-2 bg-[#005CB6] rounded-full animate-bounce" style={{animationDelay: '300ms'}} />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-3 bg-white border-t border-gray-100 flex-shrink-0">
            <form
              onSubmit={(e) => { e.preventDefault(); sendMessage(input); }}
              className="flex items-center gap-2"
            >
              <input
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                placeholder="Posez votre question..."
                className="flex-1 bg-gray-100 border-0 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#005CB6]/30 placeholder:text-gray-400"
                disabled={loading}
                data-testid="assistant-input"
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                className="w-10 h-10 rounded-xl bg-[#005CB6] text-white flex items-center justify-center hover:bg-[#004a94] transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
                data-testid="assistant-send"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default AssistantWidget;
